import React, {
	type PropsWithChildren,
	type RefObject,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { di } from 'react-magnetic-di';
import { defaults as ReactSweetStateDefaults } from 'react-sweet-state';

import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import UFOSuspense from '@atlaskit/react-ufo/suspense';
import { useInteractionContext } from '@atlaskit/react-ufo/use-interaction-context';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { AgentOverriderProvider } from '@atlassian/conversation-assistant-agent/controllers/agent-overrider';
import {
	RovoEntitlementSetter,
	useRovoEntitlement,
} from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { AnalyticsContextWithSourceAndProperties } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { useUFOLabelStackSetter } from '@atlassian/conversation-assistant-instrumentation/main';
import {
	AssistanceServiceImpl,
	AssistanceServiceProvider,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';
import {
	useUIStore,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { AifcDebugger } from '@atlassian/smart-creation/aifc-debugger';

import { AI_MATE_EXPERIENCE_ID } from './common/constants/private';
import { ErrorBoundary as AIMateErrorBoundary } from './common/ui/error-boundary';
import { createGlobalConfig } from './common/utils/global-config/createGlobalConfig';
import { GlobalConfigProvider } from './common/utils/global-config/GlobalConfigProvider';
import { type GlobalConfig } from './common/utils/global-config/types';
import { setRovoChatProduct } from './common/utils/product-config';
import { SideNavProvider } from './common/utils/side-nav-context/SideNavProvider';
import { UIConfigProvider } from './common/utils/ui-config/UIConfigProvider';
import { AgentVersioningConfigProvider } from './controllers/agent-versioning-config/AgentVersioningConfigProvider';
import { AnalyticsPropertiesUpdater } from './controllers/analytics';
import { BridgeAPIHandlers } from './controllers/bridge-api/handlers';
import { ChatStoreContainer } from './controllers/chat-store/ChatStoreContainer';
import { ConversationAssistantLoadingContext } from './ConversationAssistantLoadingContext';
import { ConversationAssistantUILazy } from './ConversationAssistantUILazy';
import { ConversationAssistantWrapper } from './ConversationAssistantWrapper';
import { getRovoChatXpcContextValues } from './getRovoChatXpcContextValues';
import { useConversationAssistantProviderValues } from './provider-value-stabilization';
import { RovoChatXpcContextOverride } from './RovoChatXpcContextOverride';
import { RovoTargetingScope } from './RovoTargetingScope';
import { type ConversationAssistantProps, ConversationAssistantUIModes } from './types';
import { RovoChatUFOStatePublisher } from './ui/ufo-state-publisher';

export type ConversationAssistantConfigurerProps = ConversationAssistantProps & {
	isNav4Enabled?: boolean;
	stagingAreaRef?: RefObject<HTMLDivElement>;
};

const ChatUILoadingBoundary = ({ children }: PropsWithChildren): JSX.Element => {
	const hasOuterLoadingBoundary = useContext(ConversationAssistantLoadingContext);
	if (hasOuterLoadingBoundary) {
		return <>{children}</>;
	}

	return (
		<UFOSuspense fallback={null} interactionName="LoadingConversationAssistantUIBundle">
			{children}
		</UFOSuspense>
	);
};

/**
 * Chat store / provider shell. It renders the chat UI through the `ConversationAssistantUILazy`
 * boundary and never pre-fetches it: consumers that mount this only to initialise the global Rovo
 * store and register the bridge API handlers — Confluence's app root, the CwR staging-area modal,
 * the remix containers — never render `ConversationAssistantRender`, so they should not pay to
 * download or evaluate its module graph either.
 *
 * Everything a `skipChatUI` mount needs stays in this module: the entitlement setter, the provider
 * stack, `ConversationAssistantWrapper` (which owns `ChatStoreContainer`), the UFO state publisher,
 * `BridgeAPIHandlers` and `AifcDebugger`.
 */
export const ConversationAssistantConfigurer = ({
	testId,
	onCloseChat,
	config,
	uiConfig,
	agentsConfig,
	agentVersioningConfig,
	isNav4Enabled,
	sitePickerRender,
	overridingChildren,
	modeConfig,
	getActions,
	prioritizeCustomActionResults,
	numStartersToShow,
	stagingAreaRef,
	topOffset = 0,
	tags,
	initialPrompt,
	suppressInitialFocus,
	skipChatUI,
	forceRestoreConversationId,
}: ConversationAssistantConfigurerProps): JSX.Element => {
	di(
		AssistanceServiceProvider,
		ChatStoreContainer,
		SmartCardProvider,
		AnalyticsContextWithSourceAndProperties,
	);

	const shouldSkipChatUI = skipChatUI ?? Boolean(overridingChildren);

	useMemo(() => {
		// Setting devtools in useMemo to make sure we catch the first render.
		// ReactSweetStateDefaults may be undefined when bundlers tree-shake or when the
		// module isn't available at runtime. Guard access to avoid runtime exceptions.
		try {
			if (
				// eslint-disable-next-line @atlaskit/platform/no-preconditioning
				fg('ai_mate_react_sweet_state_devtools') &&
				typeof ReactSweetStateDefaults !== 'undefined' &&
				ReactSweetStateDefaults &&
				'devtools' in ReactSweetStateDefaults
			) {
				ReactSweetStateDefaults.devtools = true;
			}
		} catch {
			// swallow errors intentionally — best-effort only
		}
	}, []);

	const ufoContext = useInteractionContext();
	const ufoLabelStack = useMemo(
		() => ufoContext?.labelStack ?? [{ name: 'rovo-conversation-assistant' }],
		[ufoContext?.labelStack],
	);
	const setUFOLabelStack = useUFOLabelStackSetter();
	useMemo(() => {
		setUFOLabelStack(ufoLabelStack);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ufoLabelStack]);

	const profileData = useProfileController();
	const { isRovoEnabled } = useRovoEntitlement();
	const { setIsChatOnboardingMiniModalOpen } = useUIStoreActions();
	const { isChatOnboardingMiniModalOpen } = useUIStore();

	useEffect(() => {
		setRovoChatProduct(config.product);
	}, [config.product]);

	const [sharedFeaturesInstrumentationId, setSharedFeaturesInstrumentationId] = useState<
		string | undefined
	>(undefined);

	const assistanceService = useMemo(
		() =>
			new AssistanceServiceImpl({
				baseUrl: config.assistanceServiceConfig?.baseUrl,
				headers: config.assistanceServiceConfig?.headers,
				channelId: config.assistanceServiceConfig?.channelId,
				product: config.product,
				siteId: config.siteId,
				experienceId: config.experienceId ?? AI_MATE_EXPERIENCE_ID,
				minionAlias: config.assistanceServiceConfig?.minionAlias,
				labelStack: ufoLabelStack,
			}),
		[
			config.assistanceServiceConfig?.baseUrl,
			config.assistanceServiceConfig?.headers,
			config.assistanceServiceConfig?.channelId,
			config.product,
			config.siteId,
			config.experienceId,
			config.assistanceServiceConfig?.minionAlias,
			ufoLabelStack,
		],
	);

	const globalConfig: GlobalConfig = useMemo(
		() =>
			createGlobalConfig({
				...config,
				profileData,
				closeChat: () => {
					onCloseChat?.();

					if (isChatOnboardingMiniModalOpen) {
						setIsChatOnboardingMiniModalOpen(false);
					}
				},
			}),
		[
			config,
			profileData,
			onCloseChat,
			setIsChatOnboardingMiniModalOpen,
			isChatOnboardingMiniModalOpen,
		],
	);
	const { agentOverriderValue, uiConfigValue } = useConversationAssistantProviderValues({
		agentsConfig,
		uiConfig,
	});

	// When initialPrompt is provided, self-publish a chat-new PubSub event
	// to trigger the existing PubSubListener handler which creates a seeded
	// conversation and auto-sends the prompt.
	const initialPromptFiredRef = useRef(false);
	const publish = usePublish('ai-mate');
	useEffect(() => {
		if (!initialPrompt || initialPromptFiredRef.current) {
			return;
		}
		initialPromptFiredRef.current = true;

		// Use requestAnimationFrame to ensure PubSubListener has subscribed
		requestAnimationFrame(() => {
			publish({
				type: 'chat-new',
				source: 'linkedConfluenceDocRovoNudge',
				data: {
					prompt: initialPrompt,
					dialogues: [],
					mode: { useCurrentPageContext: config.useCurrentBrowsingContext ?? true },
				},
			});
		});
	}, [config.useCurrentBrowsingContext, initialPrompt, publish]);

	const rovoChatXpcContextValues = useMemo(
		() =>
			getRovoChatXpcContextValues({
				mode: modeConfig?.mode,
				isFullScreen: uiConfig?.fullScreen,
				xpcBridge: config.xpcBridge,
				xpcProduct: config.xpcProduct,
				xpcSubProduct: config.xpcSubProduct,
				productKey: globalConfig.productKey,
			}),
		[
			config.xpcBridge,
			config.xpcProduct,
			config.xpcSubProduct,
			globalConfig.productKey,
			modeConfig?.mode,
			uiConfig?.fullScreen,
		],
	);

	return (
		// The order of analytics properties context and experienceTracker matters here
		// for experience tracker to send correct analytics properties, it needs to be under analyticsContext
		<AnalyticsContextWithSourceAndProperties
			source="AIMate"
			mode={modeConfig?.mode ?? ConversationAssistantUIModes.SIDEBAR}
			sharedFeaturesInstrumentationId={sharedFeaturesInstrumentationId}
			isAlwaysInSmartCreateFlow={sharedFeaturesInstrumentationId !== undefined ? true : undefined}
		>
			<RovoTargetingScope
				productKey={config.rovoTargetingContext?.productKey ?? globalConfig.productKey}
				accountId={
					config.rovoTargetingContext ? config.rovoTargetingContext.accountId : config.userId
				}
				siteId={config.rovoTargetingContext ? config.rovoTargetingContext.siteId : config.siteId}
				hasRovoAccess={config.rovoTargetingContext?.hasRovoAccess ?? isRovoEnabled}
			>
				{/* The idea is if Chat is rendered, we assume the entitlement check has been done before, and we want to allow all our platform components to have isRovoEnabled
					Use case: For integrators that only has Rovo chat as their touchpoints, they don't need to render RovoEntitlementSetter
					*/}
				<RovoEntitlementSetter isRovoEnabled={isRovoEnabled} />
				<AIMateErrorBoundary
					experienceName={ExperienceName.LOAD_PANEL}
					rethrowErrors={config.rethrowErrors ?? false}
				>
					<GlobalConfigProvider value={globalConfig}>
						<AgentOverriderProvider value={agentOverriderValue}>
							<AgentVersioningConfigProvider value={agentVersioningConfig}>
								<UIConfigProvider value={uiConfigValue}>
									<SmartCardProvider
										bridgeProduct={rovoChatXpcContextValues.bridgeProduct}
										xpcProduct={rovoChatXpcContextValues.xpcProduct}
										xpcSubProduct={rovoChatXpcContextValues.xpcSubProduct}
									>
										<RovoChatXpcContextOverride {...rovoChatXpcContextValues}>
											<FlagsProvider>
												<AssistanceServiceProvider value={assistanceService}>
													<SideNavProvider>
														<RovoChatUFOStatePublisher
															fullscreen={uiConfig?.fullScreen}
															mode={modeConfig?.mode ?? ConversationAssistantUIModes.SIDEBAR}
															shouldPublishChatOpenOnTransitionData={!shouldSkipChatUI}
														/>
														<ConversationAssistantWrapper
															onPromptSent={config.onPromptSent}
															forceRestoreConversationId={forceRestoreConversationId}
														>
															{shouldSkipChatUI ? (
																// NOTE: `overridingChildren` consumers (e.g. CwR staging-area-modal,
																// chat-only-creation-modal) are responsible for wrapping their own
																// subtree with `RovoSettingsModalProvider` so entrypoints to the
																// settings modal work via useSideNavModal(). The provider is
																// idempotent, so defensive wrapping is safe. See ASIMO-3977.
																<>
																	<AnalyticsPropertiesUpdater />
																	{overridingChildren}
																</>
															) : (
																<ChatUILoadingBoundary>
																	<ConversationAssistantUILazy
																		config={config}
																		getActions={getActions}
																		isNav4Enabled={isNav4Enabled}
																		modeConfig={modeConfig}
																		numStartersToShow={numStartersToShow}
																		prioritizeCustomActionResults={prioritizeCustomActionResults}
																		setSharedFeaturesInstrumentationId={
																			setSharedFeaturesInstrumentationId
																		}
																		sitePickerRender={sitePickerRender}
																		stagingAreaRef={stagingAreaRef}
																		suppressInitialFocus={suppressInitialFocus}
																		tags={tags}
																		testId={testId}
																		topOffset={topOffset}
																		uiConfig={uiConfig}
																	/>
																</ChatUILoadingBoundary>
															)}
															<BridgeAPIHandlers />
															<AifcDebugger />
														</ConversationAssistantWrapper>
													</SideNavProvider>
												</AssistanceServiceProvider>
											</FlagsProvider>
										</RovoChatXpcContextOverride>
									</SmartCardProvider>
								</UIConfigProvider>
							</AgentVersioningConfigProvider>
						</AgentOverriderProvider>
					</GlobalConfigProvider>
				</AIMateErrorBoundary>
			</RovoTargetingScope>
		</AnalyticsContextWithSourceAndProperties>
	);
};
